import { useEffect, useState } from "react";
import { Play, Youtube } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useSettings } from "@/context/SettingsContext";

export const VideoSection = () => {
  const [videos, setVideos] = useState(null);
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const { settings, homepage } = useSettings();
  const channel = settings?.youtube_channel || "";
  const poster = (v) => `https://i.ytimg.com/vi/${v.video_id}/maxresdefault.jpg`;

  useEffect(() => {
    if (!settings) return;
    api.get("/youtube/videos", { params: { limit: homepage.videos_count || 6 } }).then(async (r) => {
      const first = r.data[0];
      if (first) await Promise.race([new Promise((res) => { const img = new Image(); img.onload = img.onerror = res; img.src = poster(first); }), new Promise((res) => setTimeout(res, 3000))]);
      setVideos(r.data); setActive(first || null);
    }).catch(() => setVideos([]));
  }, [settings, homepage.videos_count]);

  const select = (v) => { setActive(v); setPlaying(true); };

  return (
    <section id="videos" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-14" data-testid="videos-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          {homepage.videos_eyebrow && <p className="eyebrow mb-2">{homepage.videos_eyebrow}</p>}
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{homepage.videos_title || "Latest on YouTube"}</h2>
        </div>
        {channel && <a href={channel} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white" data-testid="youtube-channel-link"><Youtube size={18} className="text-rose-500" /> Subscribe</a>}
      </div>

      {!videos ? (
        <div className="grid lg:grid-cols-[1fr_360px] gap-5"><Skeleton className="aspect-video rounded-3xl bg-white/5" /><div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl bg-white/5" />)}</div></div>
      ) : videos.length === 0 ? (
        <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="videos-empty">Connect your YouTube channel in Admin → Settings to show videos here.</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_380px] gap-5 lg:items-stretch">
          <div className="card-surface rounded-3xl overflow-hidden reveal" data-testid="youtube-video-hero">
            <div className="aspect-video bg-black relative">
              {playing ? (
                <iframe key={active.video_id} src={`https://www.youtube.com/embed/${active.video_id}?rel=0&autoplay=1`} title={active.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="youtube-video-iframe" />
              ) : (
                <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" aria-label={`Play ${active.title}`} data-testid="youtube-video-poster">
                  <img src={poster(active)} onError={(e) => { if (e.currentTarget.src !== active.thumbnail) e.currentTarget.src = active.thumbnail; }} alt={active.title} className="h-full w-full object-cover" loading="eager" fetchPriority="high" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-sky-400 text-[#070F1E] shadow-2xl transition-transform duration-300 group-hover:scale-110"><Play size={34} fill="currentColor" className="ml-1" /></span>
                  </span>
                </button>
              )}
            </div>
            <div className="p-5 sm:p-6">
              <h3 className="font-display text-xl sm:text-2xl font-bold leading-snug" data-testid="youtube-video-hero-title">{active.title}</h3>
              <p className="mt-2 text-sm text-slate-400 line-clamp-2">{active.description}</p>
              <p className="mt-3 font-mono text-[11px] text-slate-500">{timeAgo(active.published_at)}</p>
            </div>
          </div>
          <div className="grid grid-rows-5 gap-3 lg:h-full lg:min-h-0" data-testid="youtube-video-list">
            {videos.filter((v) => v.video_id !== active.video_id).slice(0, 5).map((v, i) => (
              <button key={v.video_id} onClick={() => select(v)} className="card-surface card-surface-hover rounded-2xl p-3 flex gap-3 items-center text-left reveal min-h-0" style={{ animationDelay: `${i * 70}ms` }} data-testid={`youtube-video-card-${i}`}>
                <div className="relative w-36 shrink-0 aspect-video rounded-lg overflow-hidden max-h-full">
                  <img src={v.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30"><Play size={18} fill="white" className="text-white" /></span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-snug line-clamp-2">{v.title}</p>
                  <p className="mt-1.5 font-mono text-[11px] text-slate-500">{timeAgo(v.published_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
